import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import authService from "@/services/authService";
import { toast } from "sonner";
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  UserRound,
} from "lucide-react";

const REGISTER_BACKGROUND = "/images/login-gold-background.png";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.register(name, email, password, role);
      toast.success("Registration successful! Please login.");
      navigate("/login");
    } catch (error) {
      console.error("Registration error:", error);
      const message =
        error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "h-12 rounded-lg border border-[#d1a64d]/45 bg-black/30 pl-12 pr-4 text-sm text-[#f7ead2] placeholder:text-[#d8be86]/60 shadow-[inset_0_0_16px_rgba(0,0,0,0.45)] focus:border-[#ffd76d] focus:ring-2 focus:ring-[#f4c766]/25";

  return (
    <div className="gr-auth relative min-h-screen overflow-hidden bg-black text-[#f7ead2]">
      <style>{`
        .gr-auth input:-webkit-autofill,
        .gr-auth input:-webkit-autofill:hover,
        .gr-auth input:-webkit-autofill:focus {
          -webkit-text-fill-color: #f7ead2;
          caret-color: #f7ead2;
          box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.42) inset, inset 0 0 16px rgba(0, 0, 0, 0.45);
          transition: background-color 9999s ease-out 0s;
        }
      `}</style>
      <img
        src={REGISTER_BACKGROUND}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(232,173,49,0.03),rgba(0,0,0,0.24)_48%,rgba(0,0,0,0.7))]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 to-transparent" />

      <main className="relative z-10 flex min-h-screen flex-col items-center justify-center px-5 py-8 sm:px-8">
        <section className="relative w-full max-w-[500px] pt-9">
          <div className="absolute left-1/2 top-0 z-20 flex h-[72px] w-[72px] -translate-x-1/2 items-center justify-center rounded-[20px] border border-[#f8ca62]/70 bg-black/55 shadow-[0_0_34px_rgba(232,179,67,0.58),inset_0_0_22px_rgba(232,179,67,0.18)] backdrop-blur-md sm:h-20 sm:w-20">
            <BookOpen className="h-9 w-9 text-[#ffd76d] drop-shadow-[0_0_12px_rgba(255,215,109,0.8)] sm:h-10 sm:w-10" />
          </div>

          <div
            className="rounded-[24px] border border-[#f4c766]/70 bg-black/50 px-6 pb-7 pt-11 shadow-[0_0_48px_rgba(214,158,46,0.34),inset_0_1px_22px_rgba(255,223,137,0.12)] backdrop-blur-xl sm:px-8"
            data-testid="register-card"
          >
            <div className="text-center">
              <h1
                className="text-3xl font-bold leading-tight text-[#f1bd46] sm:text-4xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Join Golden Records
              </h1>
              <p className="mt-2 text-sm text-[#f7ead2]/85">
                Create an account to get started
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              autoComplete="off"
              className="mt-6 space-y-4"
              data-testid="register-form"
            >
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm text-[#f7ead2]">
                  Full Name
                </Label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <Input
                    data-testid="register-name-input"
                    id="name"
                    type="text"
                    autoComplete="off"
                    placeholder=""
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-[#f7ead2]">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <Input
                    data-testid="register-email-input"
                    id="email"
                    type="email"
                    placeholder=""
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm text-[#f7ead2]">
                  Account Type
                </Label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="h-12 w-full appearance-none rounded-lg border border-[#d1a64d]/45 bg-black/30 pl-12 pr-12 text-sm text-[#f7ead2] shadow-[inset_0_0_16px_rgba(0,0,0,0.45)] outline-none transition focus:border-[#ffd76d] focus:ring-2 focus:ring-[#f4c766]/25"
                  >
                    <option value="user" className="bg-[#080604] text-[#f7ead2]">
                      User
                    </option>
                    <option
                      value="narrator"
                      className="bg-[#080604] text-[#f7ead2]"
                    >
                      Narrator
                    </option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                </div>
                <p className="text-xs leading-relaxed text-[#f7ead2]/60">
                  Users can explore and participate. Narrators can create voice
                  profiles and receive assignments.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-[#f7ead2]">
                  Password
                </Label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <Input
                    data-testid="register-password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className={`${inputClass} pr-14`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-[#f4c766] transition hover:bg-[#f4c766]/10 hover:text-[#ffd76d]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-[#f7ead2]/60">
                  Must be at least 8 characters
                </p>
              </div>

              <Button
                data-testid="register-submit-button"
                type="submit"
                className="h-12 w-full rounded-lg bg-gradient-to-r from-[#a56e0a] via-[#f0c055] to-[#a66b08] text-sm font-bold text-black shadow-[0_0_24px_rgba(234,176,55,0.38),inset_0_1px_0_rgba(255,255,255,0.35)] transition-all duration-300 hover:brightness-110"
                disabled={loading}
              >
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>

            <div className="mt-7 text-center">
              <p className="text-sm text-[#f7ead2]/80">
                Already have an account?{" "}
                <button
                  data-testid="register-login-link"
                  onClick={() => navigate("/login")}
                  className="font-semibold text-[#f4c766] hover:text-[#ffd76d]"
                >
                  Login
                </button>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-home-link"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-base font-medium text-[#f4c766] transition hover:text-[#ffd76d]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </button>
          </div>
        </section>
      </main>

      <div className="pointer-events-none absolute bottom-8 right-8 hidden items-center gap-4 text-[#dba63a] drop-shadow-[0_0_18px_rgba(219,166,58,0.45)] md:flex">
        <div className="relative h-14 w-14 rounded-full border-2 border-[#dba63a]/80">
          <div className="absolute inset-2 rounded-full border border-[#dba63a]/70" />
          <div className="absolute inset-5 rounded-full bg-[#dba63a]" />
        </div>
        <div className="leading-none tracking-[0.38em]">
          <p className="text-xl font-semibold">GOLDEN</p>
          <p className="mt-2 text-sm">RECORDS</p>
        </div>
      </div>
    </div>
  );
}
